/**
 * Data section (spec sections 57, 64, 68).
 *
 * Sync Status is a Phase 1 acceptance criterion in its own right ("Sync
 * failures are visible in an admin/status screen"), so it is built for real.
 * Freshness reads the same source and presents it as clocks rather than as job
 * history, because those are two different questions: "did the job run" and
 * "how old is the number I am about to act on".
 */

import { Link } from 'react-router-dom';
import type { StrategyRule, SyncStatus } from '@kso/schemas';
import { useStrategyRules, useSyncStatus } from '../../api/queries';
import { useAuth } from '../../lib/auth';
import { useCoreContext } from '../../lib/coreAlliance';
import { Badge, Card, Notice, PageHeader, Stat, Value } from '../../components/ui/primitives';
import { EmptyState, NotAvailableYet, QueryBoundary } from '../../components/ui/states';
import { LanguageToggle } from '../../components/layout/LanguageToggle';
import { formatDateTime, formatDuration, formatNumber } from '../../lib/format';
import { useT } from '../../i18n';

function ageSeconds(iso: string | null): number | null {
  if (!iso) return null;
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return null;
  return Math.max(0, (Date.now() - at) / 1000);
}

export function SyncStatusPage() {
  const t = useT();
  const status = useSyncStatus();

  return (
    <>
      <PageHeader title={t.data.sync.title} subtitle={t.data.sync.subtitle} />
      <QueryBoundary query={status} loadingLabel={t.data.sync.loading}>
        {(data) => <SyncStatusBody status={data} />}
      </QueryBoundary>
    </>
  );
}

function SyncStatusBody({ status }: { status: SyncStatus }) {
  const t = useT();
  const budgetUsed =
    status.dailyRequestBudget > 0 ? status.dailyRequestsUsed / status.dailyRequestBudget : null;

  const kingdomAge = ageSeconds(status.lastKingdomSync);
  const allianceAge = ageSeconds(status.lastAllianceSync);

  return (
    <div className="stack">
      {budgetUsed !== null && budgetUsed > 0.85 ? (
        <Notice severity="CRITICAL">
          {t.data.sync.budgetCritical(Math.round(budgetUsed * 100))}
        </Notice>
      ) : null}

      {status.rateLimitedToday > 0 ? (
        <Notice severity="WARN">{t.data.sync.rateLimited(status.rateLimitedToday)}</Notice>
      ) : null}

      <div className="grid grid--stats">
        <Stat
          label={t.data.sync.requestsUsed}
          value={t.data.sync.requestsUsedValue(
            formatNumber(status.dailyRequestsUsed) ?? '',
            formatNumber(status.dailyRequestBudget) ?? '',
          )}
          foot={budgetUsed === null ? null : t.data.sync.budgetFoot(Math.round(budgetUsed * 100))}
        />
        <Stat label={t.data.sync.rateLimitedToday} value={formatNumber(status.rateLimitedToday)} />
        <Stat label={t.data.sync.queueDepth} value={formatNumber(status.queueDepth)} />
        <Stat
          label={t.data.sync.lastKingdomSync}
          value={formatDateTime(status.lastKingdomSync)}
          foot={kingdomAge === null ? null : t.data.sync.ago(formatDuration(kingdomAge) ?? '')}
          absentLabel={t.data.sync.neverRun}
        />
        <Stat
          label={t.data.sync.lastAllianceSync}
          value={formatDateTime(status.lastAllianceSync)}
          foot={allianceAge === null ? null : t.data.sync.ago(formatDuration(allianceAge) ?? '')}
          absentLabel={t.data.sync.neverRun}
        />
      </div>

      <Card title={t.data.sync.failuresTitle}>
        {status.recentFailures.length === 0 ? (
          <p className="muted small" style={{ margin: 0 }}>
            {t.data.sync.noFailures}
          </p>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>{t.data.sync.failureColumns.finished}</th>
                  <th>{t.data.sync.failureColumns.job}</th>
                  <th>{t.data.sync.failureColumns.resource}</th>
                  <th>{t.data.sync.failureColumns.kind}</th>
                  <th>{t.data.sync.failureColumns.message}</th>
                </tr>
              </thead>
              <tbody>
                {status.recentFailures.map((failure, index) => (
                  <tr key={`${failure.jobType}-${failure.resourceKey}-${index}`}>
                    <td className="nowrap">{formatDateTime(failure.finishedAt)}</td>
                    {/* Job type, resource key and the Worker's sanitized error
                        message are operational identifiers, shown verbatim. */}
                    <td>{failure.jobType}</td>
                    <td className="mono small">{failure.resourceKey}</td>
                    <td>
                      {/* A rate limit and a 5xx are the upstream asking us to
                          wait, not a fault of ours; they recover on their own
                          and should not read as red alongside a real break. */}
                      <Badge
                        tone={
                          failure.errorKind === 'RATE_LIMIT' || failure.errorKind === 'UNAVAILABLE'
                            ? 'MEDIUM'
                            : 'danger'
                        }
                      >
                        {failure.errorKind ?? t.data.sync.unknownKind}
                      </Badge>
                    </td>
                    <td className="small">
                      {failure.errorMessage ?? (
                        <span className="absent">{t.data.sync.noMessage}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title={t.data.sync.staleTitle}>
        <p className="muted small" style={{ marginTop: 0 }}>
          {t.data.sync.staleBlurb}
        </p>
        {status.stalePlayers.length === 0 ? (
          <p className="muted small" style={{ margin: 0 }}>
            {t.data.sync.nothingBehind}
          </p>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>{t.data.sync.staleColumns.player}</th>
                  <th className="num">{t.data.sync.staleColumns.age}</th>
                </tr>
              </thead>
              <tbody>
                {[...status.stalePlayers]
                  .sort((a, b) => b.ageSeconds - a.ageSeconds)
                  .map((player) => (
                    <tr key={player.playerId}>
                      <td>
                        <Link to={`/players/${encodeURIComponent(player.playerId)}`}>
                          {player.playerName ?? player.playerId}
                        </Link>
                      </td>
                      <td className="num">{formatDuration(player.ageSeconds)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

export function FreshnessPage() {
  const t = useT();
  const status = useSyncStatus();

  return (
    <>
      <PageHeader title={t.data.freshness.title} subtitle={t.data.freshness.subtitle} />

      {/* Spec section 64: no single global "last updated", in any language. */}
      <Notice severity="INFO">{t.data.freshness.notice}</Notice>

      <div style={{ marginTop: 12 }}>
        <QueryBoundary query={status} loadingLabel={t.data.freshness.loading}>
          {(data) => {
            const kingdomAge = ageSeconds(data.lastKingdomSync);
            const allianceAge = ageSeconds(data.lastAllianceSync);
            return (
              <div className="stack">
                <div className="grid grid--stats">
                  <Stat
                    label={t.data.freshness.kingdom}
                    value={
                      kingdomAge === null
                        ? null
                        : t.data.sync.ago(formatDuration(kingdomAge) ?? '')
                    }
                    foot={formatDateTime(data.lastKingdomSync)}
                    absentLabel={t.data.freshness.neverFetched}
                  />
                  <Stat
                    label={t.data.freshness.allianceRoster}
                    value={
                      allianceAge === null
                        ? null
                        : t.data.sync.ago(formatDuration(allianceAge) ?? '')
                    }
                    foot={formatDateTime(data.lastAllianceSync)}
                    absentLabel={t.data.freshness.neverFetched}
                  />
                </div>

                <Card title={t.data.freshness.perPlayerTitle}>
                  <p className="muted small" style={{ marginTop: 0 }}>
                    {t.data.freshness.perPlayerBlurb}
                  </p>
                  {data.stalePlayers.length === 0 ? (
                    <p className="muted small" style={{ margin: 0 }}>
                      {t.data.freshness.nonePending}
                    </p>
                  ) : (
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {data.stalePlayers.map((player) => (
                        <li key={player.playerId}>
                          <Link to={`/players/${encodeURIComponent(player.playerId)}`}>
                            {player.playerName ?? player.playerId}
                          </Link>{' '}
                          <span className="muted">
                            {t.data.freshness.playerAge(formatDuration(player.ageSeconds) ?? '')}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              </div>
            );
          }}
        </QueryBoundary>
      </div>
    </>
  );
}

/**
 * How sure we are, as a word rather than a number.
 *
 * The bands are the ones migration 014 documents, so the badge and the notes
 * cannot drift apart. A null confidence is NOT the same as zero: null means
 * nobody has scored the rule, zero means it was scored and refuted.
 */
function confidenceTone(confidence: number | null): { tone: string; key: keyof typeof BANDS } {
  if (confidence === null) return { tone: 'UNKNOWN', key: 'unscored' };
  if (confidence >= 0.8) return { tone: 'HIGH', key: 'strong' };
  if (confidence >= 0.6) return { tone: 'MEDIUM', key: 'fair' };
  if (confidence >= 0.4) return { tone: 'LOW', key: 'weak' };
  return { tone: 'UNKNOWN', key: 'poor' };
}

const BANDS = { strong: 1, fair: 1, weak: 1, poor: 1, unscored: 1 } as const;

function RuleRow({ rule }: { rule: StrategyRule }) {
  const t = useT();
  const band = confidenceTone(rule.confidence);

  return (
    <div className="state-block" style={{ marginBottom: 10 }}>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <strong>{rule.ruleKey}</strong>
        <span className="mono small muted">{rule.version}</span>
        {rule.active ? (
          <Badge tone="HIGH">{t.data.rules.active}</Badge>
        ) : (
          <Badge tone="UNKNOWN">{t.data.rules.superseded}</Badge>
        )}
        <Badge tone={band.tone} title={t.data.rules.confidenceTitle}>
          {rule.confidence === null
            ? t.data.rules.unscored
            : t.data.rules.confidenceLabel(rule.confidence)}
        </Badge>
        {rule.verified ? <Badge tone="HIGH">{t.data.rules.verified}</Badge> : null}
        {rule.sourceType ? <span className="muted small">{rule.sourceType}</span> : null}
      </div>

      {rule.notes ? (
        <p className="small" style={{ marginTop: 8, marginBottom: 0 }}>
          {rule.notes}
        </p>
      ) : null}

      <details style={{ marginTop: 8 }}>
        <summary className="small muted">{t.data.rules.showConfig}</summary>
        <pre className="mono small" style={{ marginTop: 6, overflowX: 'auto' }}>
          {JSON.stringify(rule.config, null, 2)}
        </pre>
      </details>

      <div className="row small muted" style={{ gap: 10, marginTop: 6, flexWrap: 'wrap' }}>
        {rule.sourceUrl ? (
          <a href={rule.sourceUrl} target="_blank" rel="noreferrer noopener">
            {t.data.rules.source}
          </a>
        ) : (
          <span className="absent">{t.data.rules.noSource}</span>
        )}
        <span>
          {t.data.rules.effectiveFrom}{' '}
          <Value absent={t.common.notRecorded}>{formatDateTime(rule.effectiveFrom)}</Value>
        </span>
      </div>
    </div>
  );
}

export function RulesPage() {
  const t = useT();
  const rules = useStrategyRules();

  return (
    <>
      <PageHeader title={t.data.rules.title} subtitle={t.data.rules.subtitle} />
      <Notice severity="INFO">{t.data.rules.notice}</Notice>

      <div className="stack" style={{ marginTop: 12 }}>
        <QueryBoundary
          query={rules}
          loadingLabel={t.data.rules.loading}
          isEmpty={(data) => data.rules.length === 0}
          empty={<EmptyState title={t.data.rules.noRulesTitle} body={t.data.rules.noRulesBody} />}
        >
          {(data) => {
            // Grouped by event because that is how an operator arrives here --
            // "what do we know about Bear Trap" rather than "show me rule 14".
            const byEvent = new Map<string, StrategyRule[]>();
            for (const rule of data.rules) {
              const list = byEvent.get(rule.eventType) ?? [];
              list.push(rule);
              byEvent.set(rule.eventType, list);
            }

            return (
              <>
                {[...byEvent.entries()].map(([eventType, eventRules]) => (
                  <Card key={eventType} title={eventType}>
                    {eventRules.map((rule) => (
                      <RuleRow key={rule.id} rule={rule} />
                    ))}
                  </Card>
                ))}
              </>
            );
          }}
        </QueryBoundary>
      </div>
    </>
  );
}

export function CatalogPage() {
  const t = useT();
  return (
    <>
      <PageHeader title={t.data.catalog.title} subtitle={t.data.catalog.subtitle} />
      <NotAvailableYet
        feature={t.data.catalog.feature}
        phase={t.data.catalog.phase}
        describes={t.data.catalog.describes}
      />
    </>
  );
}

export function SettingsPage() {
  const t = useT();
  const { config, state } = useAuth();
  const core = useCoreContext();

  return (
    <>
      <PageHeader title={t.data.settings.title} subtitle={t.data.settings.subtitle} />

      <div className="stack">
        <Card title={t.data.settings.connection}>
          <dl className="kv">
            <dt>{t.data.settings.apiBaseUrl}</dt>
            <dd className="mono">{config.apiBaseUrl}</dd>
            <dt>{t.data.settings.supabaseProject}</dt>
            <dd className="mono">{config.supabaseUrl}</dd>
            <dt>{t.data.settings.signedInAs}</dt>
            <dd>
              {state.status === 'signed-in' ? (
                state.session.user.email ?? state.session.user.id
              ) : (
                <span className="absent">{t.data.settings.notSignedIn}</span>
              )}
            </dd>
            <dt>{t.data.settings.kingdom}</dt>
            <dd>
              {core.kingdomId ?? <span className="absent">{t.data.settings.notConfigured}</span>}
            </dd>
            <dt>{t.data.settings.coreAlliance}</dt>
            <dd>
              {core.allianceLabel ?? (
                <span className="absent">{t.data.settings.notConfigured}</span>
              )}
            </dd>
            {/*
              Language is the one thing on this screen a member can change, and
              it is a per-browser reading preference rather than workspace
              configuration — which is why it lives here next to the read-only
              connection facts rather than in an editable settings API.
            */}
            <dt>{t.data.settings.language}</dt>
            <dd>
              <LanguageToggle />
            </dd>
          </dl>
        </Card>

        <Card title={t.data.settings.whereConfigTitle}>
          <p className="muted small" style={{ marginTop: 0 }}>
            {t.data.settings.whereConfigBody}
          </p>
          <p className="muted small" style={{ marginBottom: 0 }}>
            {t.data.settings.tokenNote}
          </p>
        </Card>

        <EmptyState
          title={t.data.settings.notEditableTitle}
          body={t.data.settings.notEditableBody}
        />
      </div>
    </>
  );
}
